import { BehaviorSubject, catchError, defer, Observable, of, shareReplay } from 'rxjs';

import { Injectable } from '@angular/core';
import { Geolocation, Position } from '@capacitor/geolocation';
import { Platform } from '@ionic/angular';

const WATCH_POSITION_INTERVAL = 3000;

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private locationSubject = new BehaviorSubject<Position | null>(null);
  currentLocation$: Observable<Position | null> = this.locationSubject.asObservable().pipe(
    // eslint-disable-next-line rxjs-x/no-sharereplay
    shareReplay(1),
    catchError(() => of(null)),
  );

  constructor(private platform: Platform) {}

  /**
   * Check if location permission is granted
   */
  async checkLocationPermission(): Promise<void> {
    const permissions = await Geolocation.checkPermissions();
    if (this.platform.is('mobile') && permissions.location !== 'granted') {
      await Geolocation.requestPermissions();
    }
  }

  /**
   *  Get current location from device and return observable with position
   * @returns
   */
  getCurrentLocation(): Observable<Position> {
    return defer(() => Geolocation.getCurrentPosition());
  }

  /**
   * Watch location position. It will set the location subject to the position everytime it changes.
   * @returns
   */
  async watchLocationPosition(): Promise<string> {
    const positionOptions: PositionOptions = {
      enableHighAccuracy: true,
      timeout: WATCH_POSITION_INTERVAL,
      maximumAge: 0,
    };

    // Watch current location the getCurrentPosition way by setting a manual interval
    // setInterval(async () => {
    //   const position = await Geolocation.getCurrentPosition(positionOptions);
    //   this.locationSubject.next(position);
    // }, WATCH_POSITION_INTERVAL);

    // Watch current location the watchPosition way
    return await Geolocation.watchPosition(positionOptions, (position) => {
      this.locationSubject.next(position);
    });
  }

  async centerPosition(): Promise<GeolocationPosition | null> {
    try {
      // Check current permission status
      const permissions = await Geolocation.checkPermissions();

      // If permissions are denied, try to request them again
      if (this.platform.is('mobile') && permissions.location !== 'granted') {
        const newPermissions = await Geolocation.requestPermissions();
        if (newPermissions.location !== 'granted') {
          throw new Error('Location permission denied');
        }
      }

      // If permission is granted or prompt, try to get location
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve(position);
          },
          (_error) => {
            reject(new Error('Could not get location'));
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0,
          },
        );
      });
    } catch (error) {
      console.error('Error getting location:', error);
      return null;
    }
  }
}
